import { ColumnDef, Row } from "@tanstack/react-table";
import { ReactNode } from "react";
import Icon from "@/components/Icon";
import Tooltip from "@/components/Tooltip";
import { SortFieldType } from "./sortUtils";
import type { MaterialNames } from "@/components/material_symbol_names";

export interface IconConfig {
  key?: string;
  name: MaterialNames;
  size?: number;
  className?: string;
  tooltip?: {
    title?: string | ReactNode;
    supportingText: ReactNode;
    variant?: "plain" | "rich";
    color?: "white" | "black";
    positioning?: "absolute" | "fixed";
    anchorCorner?: "start-start" | "start-end" | "end-start" | "end-end";
    menuCorner?: "start-start" | "start-end" | "end-start" | "end-end";
    closeDelay?: number;
    compactHoverArea?: boolean;
  };
}

interface BuildIconTextColumnProps<T> {
  accessorKey: string;
  header: string;
  getText: (row: T) => string | ReactNode;
  getIcon?: (row: T) => IconConfig | (IconConfig | null | undefined)[] | null | undefined;
  iconPosition?: "before" | "after";
  size?: number;
  minSize?: number;
  sortType?: SortFieldType;
  textClassName?: string;
  enableSorting?: boolean;
  meta?: Record<string, any>;
}

export function buildIconTextColumn<T>({
  accessorKey,
  header,
  getText,
  getIcon,
  iconPosition = "before",
  size,
  minSize,
  sortType,
  textClassName = "",
  enableSorting,
  meta
}: BuildIconTextColumnProps<T>): ColumnDef<T> {
  const CellComponent = ({ row }: { row: Row<T> }) => {
    const iconConfig = getIcon?.(row.original);
    const iconConfigs = (Array.isArray(iconConfig) ? iconConfig : [iconConfig]).filter(Boolean) as IconConfig[];
    const text = getText(row.original);

    const displayText =
      text === null || text === undefined || text === "" ? <span className="opacity-20">--</span> : text;

    const renderIcon = (iconConfig: IconConfig) => {
      const icon = <Icon name={iconConfig.name} size={iconConfig.size || 24} className={iconConfig.className} />;
      const accessibleTooltipText =
        typeof iconConfig.tooltip?.title === "string"
          ? iconConfig.tooltip.title
          : typeof iconConfig.tooltip?.supportingText === "string"
            ? iconConfig.tooltip.supportingText
            : undefined;

      return iconConfig.tooltip ? (
        <Tooltip
          variant={iconConfig.tooltip.variant || "plain"}
          title={iconConfig.tooltip.title}
          supportingText={iconConfig.tooltip.supportingText}
          color={iconConfig.tooltip.color || "black"}
          positioning={iconConfig.tooltip.positioning}
          menuCorner={iconConfig.tooltip.menuCorner || "end-start"}
          anchorCorner={iconConfig.tooltip.anchorCorner || "start-start"}
          closeDelay={iconConfig.tooltip.closeDelay ?? 500}
          compactHoverArea={iconConfig.tooltip.compactHoverArea ?? true}
        >
          <span aria-label={accessibleTooltipText} className="inline-flex">
            {icon}
          </span>
        </Tooltip>
      ) : (
        <span className="inline-flex">{icon}</span>
      );
    };

    const iconElements = iconConfigs.map((iconConfig, index) => ({
      key: iconConfig.key ?? `${iconConfig.name}-${iconConfig.className ?? iconConfig.size ?? "icon"}-${index}`,
      element: renderIcon(iconConfig)
    }));

    return (
      <div className="flex items-center">
        {iconElements.length > 0 &&
          iconPosition === "before" &&
          iconElements.map(({ key, element }) => (
            <span key={key} className="mr-1 flex items-center">
              {element}
            </span>
          ))}
        <span className={textClassName}>{displayText}</span>
        {iconElements.length > 0 &&
          iconPosition === "after" &&
          iconElements.map(({ key, element }) => (
            <span key={key} className="ml-2 flex items-center">
              {element}
            </span>
          ))}
      </div>
    );
  };

  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize: minSize ?? size }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
